// services/smartcoin-analytics.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, tap, of } from 'rxjs';
import { environment } from 'src/environments/environment';

// ========================================
// INTERFACES
// ========================================

export interface SmartCoinOverview {
  currentBalance: number;
  currentBalanceEuros: number;
  totalEarned: number;
  totalEarnedEuros: number;
  totalSpent: number;
  totalSpentEuros: number;
  netChange: number;
  netChangeEuros: number;
  period: string;
  trends: {
    earned: number;
    spent: number;
    balance: number;
  };
  totalCoinsIssued: number;
  totalCoinsRedeemed: number;
}

export interface SmartCoinChartData {
  historical: Array<{
    date?: string;      // For daily
    month?: string;     // For monthly
    earned: number;
    spent: number;
    net_change: number;
    balance_end: number;
  }>;
  forecast: Array<{
    month: string;
    earned: number;
    spent: number;
    net_change: number;
    balance_forecast: number;
    earned_low: number;
    earned_high: number;
    spent_low: number;
    spent_high: number;
  }>;
  period: string;
  granularity: 'daily' | 'monthly';
}

export interface SmartCoinTransaction {
  id: number;
  transaction_type: string;
  amount: number;
  amount_in_euros: number;
  balance_before: number;
  balance_after: number;
  description: string;
  user_id?: string;
  user_name?: string;
  user_email?: string;
  reference_type?: string;
  reference_id?: string;
  created_at: string;
}

export interface SmartCoinTransactionsResponse {
  transactions: SmartCoinTransaction[];
  count: number;
  limit: number;
  type: string;
}

// Cache Entry Interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  period?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SmartCoinAnalyticsService {
  private baseUrl = `${environment.apiUrl}/business/smartcoin/analytics`;
  
  // Cache subjects for real-time updates
  private overviewCache$ = new BehaviorSubject<SmartCoinOverview | null>(null);
  private chartDataCache$ = new BehaviorSubject<SmartCoinChartData | null>(null);
  private transactionsCache$ = new BehaviorSubject<SmartCoinTransaction[] | null>(null);
  
  public overview$ = this.overviewCache$.asObservable();
  public chartData$ = this.chartDataCache$.asObservable();
  public transactions$ = this.transactionsCache$.asObservable();

  // Cache storage with TTL
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  /**
   * Check if cache entry is still valid
   */
  private isCacheValid(key: string, period?: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;

    const now = Date.now();
    const isExpired = (now - entry.timestamp) > this.CACHE_TTL;
    const periodMatches = !period || entry.period === period;

    return !isExpired && periodMatches;
  }

  /**
   * Set cache entry
   */
  private setCache<T>(key: string, data: T, period?: string): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      period
    });
  }

  /**
   * Get cache entry
   */
  private getCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    return entry ? entry.data : null;
  }

  // ========================================
  // API METHODS
  // ========================================

  /**
   * Get SmartCoin overview
   * @param period '7d' | '30d' | '90d'
   * @param forceRefresh Skip cache and force API call
   */
  getOverview(period: string = '30d', forceRefresh: boolean = false): Observable<SmartCoinOverview> {
    const cacheKey = `overview_${period}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<SmartCoinOverview>(cacheKey);
      if (cached) {
        this.overviewCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams().set('period', period);
    
    return this.http.get<SmartCoinOverview>(`${this.baseUrl}/overview`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.overviewCache$.next(data);
      })
    );
  }

  /**
   * Get SmartCoin chart data (historical + forecast)
   * @param period '7d' | '30d' | '90d' | 'all'
   * @param forecast Include forecast data
   * @param forceRefresh Skip cache and force API call
   */
  getChartData(
    period: string = '30d', 
    forecast: boolean = true, 
    forceRefresh: boolean = false
  ): Observable<SmartCoinChartData> {
    const cacheKey = `chart_${period}_${forecast}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey, period)) {
      const cached = this.getCache<SmartCoinChartData>(cacheKey);
      if (cached) {
        this.chartDataCache$.next(cached);
        return of(cached);
      }
    }

    const params = new HttpParams()
      .set('period', period)
      .set('forecast', forecast.toString());
    
    return this.http.get<SmartCoinChartData>(`${this.baseUrl}/chart-data`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data, period);
        this.chartDataCache$.next(data);
      })
    );
  }

  /**
   * Get recent transactions
   * @param limit Number of transactions to fetch
   * @param type 'all' | 'EARNING' | 'REDEMPTION'
   * @param forceRefresh Skip cache and force API call
   */
  getTransactions(
    limit: number = 15, 
    type: string = 'all', 
    forceRefresh: boolean = false
  ): Observable<SmartCoinTransactionsResponse> {
    const cacheKey = `transactions_${limit}_${type}`;
    
    if (!forceRefresh && this.isCacheValid(cacheKey)) {
      const cached = this.getCache<SmartCoinTransactionsResponse>(cacheKey);
      if (cached) {
        this.transactionsCache$.next(cached.transactions);
        return of(cached);
      }
    }

    const params = new HttpParams()
      .set('limit', limit.toString())
      .set('type', type);
    
    return this.http.get<SmartCoinTransactionsResponse>(`${this.baseUrl}/transactions`, { params }).pipe(
      tap(data => {
        this.setCache(cacheKey, data);
        this.transactionsCache$.next(data.transactions);
      })
    );
  }

  /**
   * Get transaction detail
   * @param transactionId Transaction ID
   */
  getTransactionDetail(transactionId: number): Observable<SmartCoinTransaction> {
    return this.http.get<SmartCoinTransaction>(`${this.baseUrl}/transaction/${transactionId}`);
  }

  // ========================================
  // UTILITY METHODS
  // ========================================

  /**
   * Refresh all SmartCoin data
   */
  refreshAllData(period: string = '30d'): void {
    this.getOverview(period, true).subscribe();
    this.getChartData(period, true, true).subscribe();
    this.getTransactions(15, 'all', true).subscribe();
  }

  /**
   * Invalidate specific cache entry
   */
  invalidateCache(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Invalidate cache for specific period
   */
  invalidatePeriod(period: string): void {
    const keysToDelete: string[] = [];
    
    this.cache.forEach((_, key) => {
      if (key.includes(period)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Clear all cache
   */
  clearCache(): void {
    this.cache.clear();
    this.overviewCache$.next(null);
    this.chartDataCache$.next(null);
    this.transactionsCache$.next(null);
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats(): { size: number; entries: string[] } {
    const entries: string[] = [];
    this.cache.forEach((value, key) => {
      const age = Date.now() - value.timestamp;
      const ageMinutes = Math.floor(age / 60000);
      entries.push(`${key} (${ageMinutes}m old)`);
    });

    return {
      size: this.cache.size,
      entries
    };
  }

  /**
   * Format coins to euros
   */
  formatEuros(coins: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(coins / 100);
  }

  /**
   * Format transaction type for display
   */
  formatTransactionType(type: string): string {
    const types: Record<string, string> = {
      'EARNING': 'Gutschrift (Kunde verdient)',
      'REDEMPTION': 'Einnahme (Kunde zahlt)',
      'FEE': 'Gebühr',
      'BILLING': 'Abrechnung',
      'ADJUSTMENT': 'Anpassung'
    };
    return types[type] || type;
  }

  /**
   * Get transaction type color
   */
  getTransactionColor(type: string): 'success' | 'danger' | 'warning' | 'info' {
    const colors: Record<string, 'success' | 'danger' | 'warning' | 'info'> = {
      'EARNING': 'danger',     // Vendor loses coins (red)
      'REDEMPTION': 'success', // Vendor gains coins (green)
      'FEE': 'warning',
      'BILLING': 'info',
      'ADJUSTMENT': 'info'
    };
    return colors[type] || 'info';
  }
}